import React, { createContext, useContext, useState, useCallback, ReactNode, CSSProperties } from 'react';
import { useDeviceContext } from '../web-core/misc/useDevice';
import { useCombinedAuth } from '../web-core';
import { validateMnemonic, mnemonicToSeed } from '../crypto/bip39';
import { bytesToHex } from '../crypto';
import { deviceKidsForSeed } from '../wasm/ops';
import '../css/Settings.css';

// --- Reusable UI Components ---

type AlertVariant = 'success' | 'warning' | 'danger' | 'info';

const alertColors: Record<AlertVariant, { bg: string; border: string; title: string }> = {
    success: { bg: 'rgba(72, 187, 120, 0.15)', border: '#48bb78', title: '#68d391' },
    warning: { bg: 'rgba(236, 201, 75, 0.15)', border: '#ecc94b', title: '#ecc94b' },
    danger: { bg: 'rgba(252, 129, 129, 0.15)', border: '#fc8181', title: '#fc8181' },
    info: { bg: 'rgba(255, 255, 255, 0.05)', border: 'rgba(255, 255, 255, 0.1)', title: 'rgba(255, 255, 255, 0.8)' },
};

interface AlertBoxProps {
    variant: AlertVariant;
    title: string;
    children?: ReactNode;
    style?: CSSProperties;
}

const AlertBox: React.FC<AlertBoxProps> = ({ variant, title, children, style }) => {
    const colors = alertColors[variant];
    return (
        <div style={{
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            ...style,
        }}>
            <p style={{ margin: 0, color: colors.title, fontWeight: 'bold' }}>{title}</p>
            {children && (
                <div style={{ margin: '0.5rem 0 0 0', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                    {children}
                </div>
            )}
        </div>
    );
};

interface ButtonRowProps {
    children: ReactNode;
}

const ButtonRow: React.FC<ButtonRowProps> = ({ children }) => (
    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
        {children}
    </div>
);

interface RiskCheckboxProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    children: ReactNode;
}

const RiskCheckbox: React.FC<RiskCheckboxProps> = ({ checked, onChange, children }) => (
    <label style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        cursor: 'pointer',
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '0.9rem',
    }}>
        <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            style={{
                width: '18px',
                height: '18px',
                marginTop: '2px',
                flexShrink: 0,
            }}
        />
        <span>{children}</span>
    </label>
);

const BodyText: React.FC<{ children: ReactNode; style?: CSSProperties }> = ({ children, style }) => (
    <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.95rem', marginBottom: '1.5rem', ...style }}>
        {children}
    </p>
);

// --- End Reusable UI Components ---

interface DeviceInfo {
    id: string;
    name: string;
    deviceType: string;
    sigKid: string;
    isCurrentDevice: boolean;
}

type LogoutStep =
    | 'loading'
    | 'has-paper-key'      // Paper key registered - should verify they have it
    | 'has-other-devices'  // Other devices but no paper key - can use those to login
    | 'no-recovery'        // Only this device, no paper key - dangerous!
    | 'verify-paper-key'   // Enter paper key to prove possession
    | 'warning';           // Couldn't determine status

interface LogoutConfirmationContextType {
    /**
     * Request logout with confirmation dialog.
     * @param postLogoutUri - Where to redirect after logout (default: '/')
     * @param skipConfirmation - Skip the confirmation dialog (e.g., for "Not you?" flows)
     */
    requestLogout: (postLogoutUri?: string, skipConfirmation?: boolean) => void;
}

const LogoutConfirmationContext = createContext<LogoutConfirmationContextType | null>(null);

interface LogoutConfirmationProviderProps {
    children: ReactNode;
}

export const LogoutConfirmationProvider: React.FC<LogoutConfirmationProviderProps> = ({ children }) => {
    const device = useDeviceContext();
    const { authService } = useCombinedAuth();

    // Modal state
    const [isOpen, setIsOpen] = useState(false);
    const [postLogoutUri, setPostLogoutUri] = useState<string>('/');
    const [step, setStep] = useState<LogoutStep>('loading');
    const [devices, setDevices] = useState<DeviceInfo[]>([]);
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState(false);

    // Paper key verification state
    const [paperKeyInput, setPaperKeyInput] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [verificationError, setVerificationError] = useState('');

    // Risk acknowledgment checkbox
    const [acknowledgedRisk, setAcknowledgedRisk] = useState(false);

    // Track previous step for "Back" button in verify-paper-key
    const [previousStep, setPreviousStep] = useState<LogoutStep>('loading');

    const resetState = useCallback(() => {
        setIsOpen(false);
        setStep('loading');
        setPreviousStep('loading');
        setDevices([]);
        setError('');
        setPaperKeyInput('');
        setVerificationError('');
        setAcknowledgedRisk(false);
        setPostLogoutUri('/');
    }, []);

    const executeLogout = useCallback(() => {
        resetState();
        authService.logout(postLogoutUri);
    }, [authService, postLogoutUri, resetState]);

    const fetchDevices = useCallback(async () => {
        if (!device.sigKid) {
            setStep('warning');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const deviceList = await device.listDevices() as DeviceInfo[];
            setDevices(deviceList);

            // Analyze device situation
            const hasPaperKey = deviceList.some(d => d.deviceType === 'paper');
            // A passkey recovery device (Release 1) is a way back in that needs
            // no words: it counts like another device here, not like a paper
            // key whose possession the dialog asks the person to prove.
            const hasPasskey = deviceList.some(d => d.deviceType === 'passkey');
            const otherDevices = deviceList.filter(d => !d.isCurrentDevice && d.deviceType !== 'paper' && d.deviceType !== 'passkey');
            const hasOtherDevices = otherDevices.length > 0 || hasPasskey;

            // Priority: Paper key verification > Other devices > No recovery
            if (hasPaperKey) {
                // Paper key registered - but do they actually have it?
                // Require verification or explicit acknowledgment
                setStep('has-paper-key');
            } else if (hasOtherDevices) {
                // No paper key, but has other devices - can use those to login
                setStep('has-other-devices');
            } else {
                // Only this device, no paper key - dangerous!
                setStep('no-recovery');
            }
        } catch (err) {
            console.error('[LogoutConfirmation] Error fetching devices:', err);
            setError('Failed to check device status');
            setStep('warning');
        } finally {
            setLoading(false);
        }
    }, [device]);

    const requestLogout = useCallback((uri?: string, skipConfirmation?: boolean) => {
        const targetUri = uri || '/';
        setPostLogoutUri(targetUri);

        if (skipConfirmation) {
            authService.logout(targetUri);
            return;
        }

        setIsOpen(true);
        setStep('loading');
        fetchDevices();
    }, [authService, fetchDevices]);

    const handleVerifyPaperKey = async () => {
        // bip39.ts handles normalization (newlines, spaces, case)
        if (!validateMnemonic(paperKeyInput)) {
            setVerificationError('Invalid recovery phrase. Please check your words and try again.');
            return;
        }

        setVerifying(true);
        setVerificationError('');

        try {
            const seed = mnemonicToSeed(paperKeyInput);
            const paperKeyKids = await deviceKidsForSeed(bytesToHex(seed));
            seed.fill(0);

            const matchingPaperKey = devices.find(
                d => d.deviceType === 'paper' && d.sigKid === paperKeyKids.sigKid
            );

            if (matchingPaperKey) {
                executeLogout();
            } else {
                setVerificationError(
                    'This paper key is not registered to your account. ' +
                    'Please use a paper key that belongs to this account.'
                );
            }
        } catch (err) {
            console.error('[LogoutConfirmation] Paper key verification error:', err);
            setVerificationError('Failed to verify paper key. Please try again.');
        } finally {
            setVerifying(false);
        }
    };

    const hasPaperKey = devices.some(d => d.deviceType === 'paper');
    const otherDevices = devices.filter(d => !d.isCurrentDevice && d.deviceType !== 'paper' && d.deviceType !== 'passkey');
    const paperKeys = devices.filter(d => d.deviceType === 'paper');
    const passkeys = devices.filter(d => d.deviceType === 'passkey');

    return (
        <LogoutConfirmationContext.Provider value={{ requestLogout }}>
            {children}

            {/* Logout Confirmation Modal */}
            {isOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000,
                }}>
                    <div style={{
                        background: '#1a1a1a',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        padding: '2rem',
                        maxWidth: '500px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                    }}>
                        {/* Loading state */}
                        {step === 'loading' && (
                            <>
                                <h3 style={{ margin: '0 0 1rem 0', color: '#fff' }}>
                                    Checking Account Status...
                                </h3>
                                <p style={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center' }}>
                                    Verifying your recovery options before logout.
                                </p>
                            </>
                        )}

                        {/* Has paper key - require verification before logout */}
                        {step === 'has-paper-key' && (
                            <>
                                <h3 style={{ margin: '0 0 1rem 0', color: '#ecc94b' }}>
                                    Verify Your Paper Key Before Logging Out
                                </h3>

                                <AlertBox variant="warning" title="Paper key registered - but do you have it?">
                                    You have {paperKeys.length} paper key{paperKeys.length > 1 ? 's' : ''} registered.
                                    To ensure you can log back in, please verify you have access to your paper key.
                                </AlertBox>

                                <BodyText>
                                    Your paper key is the <strong>only way</strong> to access your account from a new device.
                                    If you've lost it, you should create a new one before logging out.
                                </BodyText>

                                <button
                                    className="settings-btn settings-btn-primary"
                                    onClick={() => {
                                        setPreviousStep('has-paper-key');
                                        setStep('verify-paper-key');
                                    }}
                                    style={{ width: '100%', marginBottom: '1rem' }}
                                >
                                    I Have My Paper Key - Verify & Log Out
                                </button>

                                <AlertBox variant="info" title="Lost your paper key?" style={{ marginBottom: '1rem' }}>
                                    <p style={{ margin: '0 0 0.75rem 0' }}>
                                        You should create a new one from Settings before logging out.
                                    </p>
                                    <button
                                        className="settings-btn settings-btn-secondary"
                                        onClick={resetState}
                                        style={{ width: '100%' }}
                                    >
                                        Cancel
                                    </button>
                                </AlertBox>

                                {/* Bypass option for users who are sure */}
                                <div style={{ marginBottom: '1rem' }}>
                                    <RiskCheckbox checked={acknowledgedRisk} onChange={setAcknowledgedRisk}>
                                        I understand the risks and want to log out without verifying my paper key
                                    </RiskCheckbox>
                                </div>

                                {acknowledgedRisk && (
                                    <ButtonRow>
                                        <button className="settings-btn settings-btn-danger" onClick={executeLogout}>
                                            Log Out Without Verifying
                                        </button>
                                    </ButtonRow>
                                )}
                            </>
                        )}

                        {/* Has other devices but no paper key */}
                        {step === 'has-other-devices' && (
                            <>
                                <h3 style={{ margin: '0 0 1rem 0', color: '#fff' }}>
                                    Log Out?
                                </h3>

                                <AlertBox variant="success" title={otherDevices.length > 0 ? "You have other devices logged in" : "You have a passkey"}>
                                    {otherDevices.length > 0
                                        ? `${otherDevices.length} other device${otherDevices.length > 1 ? 's' : ''} can be used to log back in.`
                                        : `Your passkey${passkeys.length > 1 ? 's' : ''} can restore this account on any of your devices.`}
                                </AlertBox>

                                <AlertBox variant="warning" title="Tip: Consider creating a paper key" style={{ background: 'rgba(236, 201, 75, 0.1)', border: '1px solid rgba(236, 201, 75, 0.3)' }}>
                                    Paper keys work even if you lose access to all your devices.
                                </AlertBox>

                                <BodyText>
                                    Logging out will remove this device's access to your account.
                                    You can log back in from your other device{otherDevices.length > 1 ? 's' : ''}.
                                </BodyText>

                                <ButtonRow>
                                    <button className="settings-btn settings-btn-secondary" onClick={resetState}>
                                        Cancel
                                    </button>
                                    <button className="settings-btn settings-btn-danger" onClick={executeLogout}>
                                        Log Out
                                    </button>
                                </ButtonRow>
                            </>
                        )}

                        {/* Warning state - couldn't determine status */}
                        {step === 'warning' && (
                            <>
                                <h3 style={{ margin: '0 0 1rem 0', color: '#ecc94b' }}>
                                    Are You Sure?
                                </h3>

                                <AlertBox variant="warning" title="Important: Device-Based Authentication">
                                    PracticeMD uses device-based authentication. Unlike email/password systems,
                                    your cryptographic keys are stored only on your devices.
                                </AlertBox>

                                <BodyText style={{ marginBottom: '0.5rem' }}>
                                    To log back in after logging out, you will need either:
                                </BodyText>
                                <ul style={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontSize: '0.9rem',
                                    paddingLeft: '1.25rem',
                                    marginBottom: '1.5rem',
                                }}>
                                    <li>Your <strong>paper key</strong> (24-word recovery phrase)</li>
                                    <li>Another device that's already logged in</li>
                                </ul>

                                {error && (
                                    <div className="settings-error-box" style={{ marginBottom: '1rem' }}>
                                        {error}
                                    </div>
                                )}

                                <ButtonRow>
                                    <button className="settings-btn settings-btn-secondary" onClick={resetState}>
                                        Cancel
                                    </button>
                                    <button className="settings-btn settings-btn-danger" onClick={executeLogout}>
                                        Log Out Anyway
                                    </button>
                                </ButtonRow>
                            </>
                        )}

                        {/* No recovery - only device, no paper key */}
                        {step === 'no-recovery' && (
                            <>
                                <h3 style={{ margin: '0 0 1rem 0', color: '#fc8181' }}>
                                    Warning: Risk of Permanent Lockout
                                </h3>

                                <AlertBox variant="danger" title="This is your only logged-in device">
                                    We did not find any paper keys or other devices registered to your account.
                                    If you log out now, you may permanently lose access to your account and data.
                                </AlertBox>

                                <BodyText style={{ marginBottom: '1rem' }}>
                                    <strong>Recommended:</strong> Create a paper key before logging out.
                                </BodyText>

                                <AlertBox variant="info" title="If you already have a paper key:">
                                    <button
                                        className="settings-btn settings-btn-primary"
                                        onClick={() => {
                                            setPreviousStep('no-recovery');
                                            setStep('verify-paper-key');
                                        }}
                                        style={{ width: '100%' }}
                                    >
                                        Verify Paper Key
                                    </button>
                                </AlertBox>

                                <div style={{ marginBottom: '1.5rem' }}>
                                    <RiskCheckbox checked={acknowledgedRisk} onChange={setAcknowledgedRisk}>
                                        I understand that logging out without a paper key may result in
                                        <strong style={{ color: '#fc8181' }}> permanent loss of access</strong> to
                                        my account and all encrypted data.
                                    </RiskCheckbox>
                                </div>

                                <ButtonRow>
                                    <button className="settings-btn settings-btn-secondary" onClick={resetState}>
                                        Cancel
                                    </button>
                                    <button
                                        className="settings-btn settings-btn-danger"
                                        onClick={executeLogout}
                                        disabled={!acknowledgedRisk}
                                        style={{ opacity: acknowledgedRisk ? 1 : 0.5 }}
                                    >
                                        Log Out Anyway
                                    </button>
                                </ButtonRow>
                            </>
                        )}

                        {/* Paper key verification step */}
                        {step === 'verify-paper-key' && (
                            <>
                                <h3 style={{ margin: '0 0 1rem 0', color: '#fff' }}>
                                    Verify Your Paper Key
                                </h3>

                                <BodyText style={{ marginBottom: '1rem' }}>
                                    Enter your 24-word paper key to verify you have a way to log back in.
                                </BodyText>

                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '0.5rem',
                                        fontWeight: 'bold',
                                        color: 'rgba(255, 255, 255, 0.8)',
                                        fontSize: '0.9rem',
                                    }}>
                                        Paper Key (24 words)
                                    </label>
                                    <textarea
                                        value={paperKeyInput}
                                        onChange={(e) => {
                                            setPaperKeyInput(e.target.value);
                                            setVerificationError('');
                                        }}
                                        placeholder="Enter your 24-word recovery phrase..."
                                        rows={4}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            borderRadius: '6px',
                                            border: '1px solid rgba(255, 255, 255, 0.15)',
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            color: '#fff',
                                            fontFamily: 'monospace',
                                            fontSize: '0.9rem',
                                            resize: 'vertical',
                                            boxSizing: 'border-box',
                                        }}
                                    />
                                </div>

                                {verificationError && (
                                    <div className="settings-error-box" style={{ marginBottom: '1rem' }}>
                                        {verificationError}
                                    </div>
                                )}

                                <ButtonRow>
                                    <button
                                        className="settings-btn settings-btn-secondary"
                                        onClick={() => {
                                            setStep(previousStep);
                                            setPaperKeyInput('');
                                            setVerificationError('');
                                        }}
                                    >
                                        Back
                                    </button>
                                    <button
                                        className="settings-btn settings-btn-primary"
                                        onClick={handleVerifyPaperKey}
                                        disabled={verifying || !paperKeyInput.trim()}
                                    >
                                        {verifying ? 'Verifying...' : 'Verify & Log Out'}
                                    </button>
                                </ButtonRow>
                            </>
                        )}
                    </div>
                </div>
            )}
        </LogoutConfirmationContext.Provider>
    );
};

/**
 * Hook to get the logout function with confirmation.
 * Use this instead of calling authService.logout directly.
 *
 * SSR-safe: Returns a no-op during server-side rendering since
 * the provider only runs in gatsby-browser.ts (browser only).
 */
export function useLogoutWithConfirmation() {
    const context = useContext(LogoutConfirmationContext);

    // During SSR, context will be null since LogoutConfirmationProvider
    // only runs in the browser (gatsby-browser.ts). Return a no-op.
    if (!context) {
        // No-op function for SSR - logout only makes sense in browser
        return (_postLogoutUri?: string, _skipConfirmation?: boolean) => {
            console.warn('[useLogoutWithConfirmation] Called during SSR - ignoring');
        };
    }

    return context.requestLogout;
}

export default LogoutConfirmationContext;
